import type { FC } from 'react';

import { initializeApolloClient } from '@crehana/apollo-client';
import { AuthTalentProvider } from '@crehana/auth';
import {
  getG11nAndTranslationsFromServer,
  type TCustomGetServerSideProps,
} from '@crehana/next';

import { AdmindashboardProvider } from '@/views/AdminDashboard/context/AdmindashboardContext';
import GET_FEATURE_FLAG from '@/views/AdminDashboard/hooks/useFlagOrganization/graphql/GetFlagOrganization.v4.b2b.graphql';
import type {
  GetFlagOrganization,
  GetFlagOrganizationVariables,
} from '@/views/AdminDashboard/hooks/useFlagOrganization/graphql/types/GetFlagOrganization';
import {
  adminLocaleEn,
  adminLocaleEs,
  adminLocalePt,
} from '@/views/AdminDashboard/i18n';
import {
  localeEn,
  localeEs,
  localePt,
} from '@/views/AdminDashboard/pages/Home/i18n';

import Home from './Home';

const PAGE_KEY = 'b2b-admin-dashboard-home';

const App: FC<{ isHomeV2Enabled: boolean }> = () => {
  return (
    <AdmindashboardProvider>
      <AuthTalentProvider>
        <Home />
      </AuthTalentProvider>
    </AdmindashboardProvider>
  );
};

// biome-ignore lint/complexity/noBannedTypes: legacy type usage
export const getServerSideProps: TCustomGetServerSideProps<{}> = async ({
  query,
  req,
}) => {
  const slug = Array.isArray(query.slug) ? query.slug[0] : query.slug;

  const apolloClient = initializeApolloClient({
    headers: req.headers,
    pageKey: PAGE_KEY,
  });
  const i18nData = await getG11nAndTranslationsFromServer({
    apolloClient,
    headers: req.headers,
    pageKey: PAGE_KEY,
    defaultTranslations: {
      es: { [PAGE_KEY]: { ...localeEs.es, ...adminLocaleEs.es } },
      pt: { [PAGE_KEY]: { ...localePt.pt, ...adminLocalePt.pt } },
      en: {
        [PAGE_KEY]: {
          ...localeEn.en,
          ...adminLocaleEn.en,
        },
      },
    },
  });

  try {
    const { data } = await apolloClient.query<
      GetFlagOrganization,
      GetFlagOrganizationVariables
    >({
      query: GET_FEATURE_FLAG,
      variables: { slug },
      context: {
        clientName: 'v4.b2b',
        headers: {
          cookie: req.headers.cookie,
        },
      },
    });

    const talentSuiteFlag = data?.b2bOrganizationCurrent?.features?.find(
      feature => feature?.slug === 'talent-suite',
    );

    if (!talentSuiteFlag) {
      return {
        redirect: {
          permanent: false,
          destination: `/org/${slug}/panel/estadisticas/generales/`,
        },
        props: {
          ...i18nData,
          organizationSlug: slug!,
        },
      };
    }

    return {
      props: {
        ...i18nData,
        data,
        organizationSlug: slug!,
      },
    };
  } catch {
    return {
      props: {
        ...i18nData,
        organizationSlug: slug!,
      },
    };
  }
};

export default App;
